import React from 'react';
import { Trans } from 'react-i18next';
import { Doughnut } from 'react-chartjs-2';

import { Ranking } from '../components/ranking/Ranking';

import { useCXIDashboard } from '../useCXIDashboard';
import { DashboardArea } from '../components/area/DashboardArea';
import { DashboardNumber } from '../components/number/DashboardNumber';

import * as Styled from '../components/StyledDashboardPage';
import * as StyledIndex from './StyledCXIDashboardIndex';

export const CXIDashboardIndex = () => {
    const { t, vars } = useCXIDashboard();
    const { company, stats } = vars;

    const totalevaluations = stats.totalEvaluations ?? 0;
    const a = stats.group?.a ?? 0;

    const graphEvaluationsData = {
        labels: ['A', 'B', 'C', 'D'],
        datasets: [
            {
                data: [
                    stats.group?.a ?? 0,
                    stats.group?.b ?? 0,
                    stats.group?.c ?? 0,
                    stats.group?.d ?? 0,
                ],
                backgroundColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
                borderColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
            },
        ],
    };
    const graphEvaluations = <Doughnut data={graphEvaluationsData} width={200} />;

    const graphStartupsData = {
        labels: ['A', 'B', 'C', 'D'],
        datasets: [
            {
                data: [
                    stats.startupsEvaluatedGroup?.a ?? 0,
                    stats.startupsEvaluatedGroup?.b ?? 0,
                    stats.startupsEvaluatedGroup?.c ?? 0,
                    stats.startupsEvaluatedGroup?.d ?? 0,
                ],
                backgroundColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
                borderColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
            },
        ],
    };
    const graphStartups = <Doughnut data={graphStartupsData} width={200} />;

    const graphPointsData = {
        labels: [
            t('dashboard.index.PointsChart.stage1'),
            t('dashboard.index.PointsChart.stage2'),
            t('dashboard.index.PointsChart.stage3'),
        ],
        datasets: [
            {
                data: [
                    stats.totalPointsStage1 ?? 0,
                    stats.totalPointsStage2 ?? 0,
                    stats.totalPointsStage3 ?? 0,
                ],
                backgroundColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
                borderColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
            },
        ],
    };
    const graphPoints = <Doughnut data={graphPointsData} width={200} />;

    const graphUsersData = {
        labels: [
            t('dashboard.index.UsersChart.withPoints'),
            t('dashboard.index.UsersChart.withoutPoints'),
        ],
        datasets: [
            {
                data: [stats.usersWithPoints ?? 0, stats.users - stats.usersWithPoints ?? 0],
                backgroundColor: ['#0094F9', '#dadada', '#4BAAB7', '#8EE2ED'],
                borderColor: ['#0094F9', '#dadada', '#4BAAB7', '#8EE2ED'],
            },
        ],
    };
    const graphUsers = <Doughnut data={graphUsersData} width={200} />;

    const graphUsersStagesData = {
        labels: [
            t('dashboard.index.Users.stage1'),
            t('dashboard.index.Users.stage2'),
            t('dashboard.index.Users.stage3'),
        ],
        datasets: [
            {
                data: [stats.usersStage1 ?? 0, stats.usersStage2 ?? 0, stats.usersStage3 ?? 0],
                backgroundColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
                borderColor: ['#0094F9', '#005995', '#4BAAB7', '#8EE2ED'],
            },
        ],
    };
    const graphUsersStages = <Doughnut data={graphUsersStagesData} width={200} />;

    return (
        <Styled.DashboardPage>
            <Styled.DashboardPageHead>
                <h2>{t('dashboard.index.title')}</h2>
            </Styled.DashboardPageHead>

            <Styled.DashboardPagePhrase>
                <Trans
                    parent="p"
                    t={t}
                    i18nKey="dashboard.index.shareable"
                    company={company.name}
                    totalevaluations={totalevaluations}
                    a={a}
                >
                    {{ company: company.name }}
                    <strong>{{ totalevaluations }} avaliações de startup</strong>
                    <strong>classificaram {{ a }} startup</strong>
                </Trans>
            </Styled.DashboardPagePhrase>

            <StyledIndex.DashboardPageAreas>
                <DashboardArea graph={graphEvaluations} title="Avaliações">
                    <DashboardNumber number={totalevaluations} active>
                        {t('dashboard.index.Evaluations.evaluations')}
                    </DashboardNumber>
                    <DashboardNumber number={stats.group?.a ?? 0}>
                        {t('dashboard.index.Evaluations.a')}
                    </DashboardNumber>
                </DashboardArea>

                <DashboardArea graph={graphStartups} title="Startups">
                    <DashboardNumber number={stats.startupsEvaluated ?? 0} active>
                        {t('dashboard.index.Startups.startups')}
                    </DashboardNumber>
                    <DashboardNumber number={stats.startupsEvaluatedGroup?.a ?? 0}>
                        {t('dashboard.index.Startups.a')}
                    </DashboardNumber>
                </DashboardArea>

                <DashboardArea graph={graphPoints} title="Pontuação">
                    <DashboardNumber number={stats.totalPoints ?? 0} active>
                        {t('dashboard.index.Points.total')}
                    </DashboardNumber>
                    <DashboardNumber>
                        <strong>{stats.totalPointsStage1 ?? 0}</strong>{' '}
                        {t('dashboard.index.Points.stage1')}
                        <br />
                        <strong>{stats.totalPointsStage2 ?? 0}</strong>{' '}
                        {t('dashboard.index.Points.stage2')}
                        <br />
                        <strong>{stats.totalPointsStage3 ?? 0}</strong>{' '}
                        {t('dashboard.index.Points.stage3')}
                        <br />
                    </DashboardNumber>
                </DashboardArea>

                <DashboardArea graph={graphUsers} title="Pessoas">
                    <DashboardNumber number={stats.users ?? 0} active>
                        {t('dashboard.index.Users.total')}
                    </DashboardNumber>
                    <DashboardNumber number={stats.usersWithPoints ?? 0}>
                        {t('dashboard.index.Users.withPoints')}
                    </DashboardNumber>
                </DashboardArea>

                <DashboardArea graph={graphUsersStages} title="Pessoas por etapa">
                    <DashboardNumber number={stats.usersStage1 ?? 0}>
                        {t('dashboard.index.Users.stage1')}
                    </DashboardNumber>
                    <DashboardNumber number={stats.usersStage2 ?? 0}>
                        {t('dashboard.index.Users.stage2')}
                    </DashboardNumber>
                    <DashboardNumber number={stats.usersStage3 ?? 0}>
                        {t('dashboard.index.Users.stage3')}
                    </DashboardNumber>
                </DashboardArea>
            </StyledIndex.DashboardPageAreas>

            <Ranking />
        </Styled.DashboardPage>
    );
};
